import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';

@Injectable({ providedIn: 'root' })
export class NachaService {

    private base = `${environment.apiUrl}/nacha`;

    constructor(private http: HttpClient) {}

    // -----------------------------------------------------------------------
    // TAX NACHA (GET / TAT / MCTAT)
    // -----------------------------------------------------------------------

    /** Preview for an explicit list of statement IDs. */
    getTaxPreview(ownerStmtIds: number[]): Observable<any[]> {
        return this.http.post<any[]>(`${this.base}/tax-preview`, { ownerStmtIds });
    }

    /** Preview for ALL statements in the given period (regardless of status). */
    getTaxPreviewByPeriod(month: number, year: number): Observable<any[]> {
        return this.http.get<any[]>(`${this.base}/tax-preview-by-period/${month}/${year}`);
    }

    /**
     * Generates the .ach file. Returns the full HttpResponse so the caller can
     * inspect headers (X-Nacha-Batch-Id) and trigger a browser download from
     * the response body blob.
     */
    generateNacha(ownerStmtIds: number[], taxType: string): Observable<HttpResponse<Blob>> {
        return this.http.post(`${this.base}/generate`,
            { ownerStmtIds, taxType },
            { observe: 'response', responseType: 'blob' }
        );
    }

    markSubmitted(nachaBatchId: number): Observable<any> {
        return this.http.put<any>(`${this.base}/batches/${nachaBatchId}/submit`, {});
    }

    listBatches(): Observable<any[]> {
        return this.http.get<any[]>(`${this.base}/batches`);
    }

    // -----------------------------------------------------------------------
    // OWNER DISTRIBUTION NACHA (BoH ePay)
    // -----------------------------------------------------------------------

    getDistPreview(ownerStmtIds: number[]): Observable<any[]> {
        return this.http.post<any[]>(`${this.base}/dist-preview`, { ownerStmtIds });
    }

    getDistPreviewByPeriod(month: number, year: number): Observable<any[]> {
        return this.http.get<any[]>(`${this.base}/dist-preview-by-period/${month}/${year}`);
    }

    /**
     * Generates the owner-distribution .ach file.
     * @param effectiveDate YYMMDD format e.g. "260512" for May 12, 2026
     */
    generateDist(ownerStmtIds: number[], effectiveDate: string): Observable<HttpResponse<Blob>> {
        return this.http.post(`${this.base}/dist-generate`,
            { ownerStmtIds, effectiveDate },
            { observe: 'response', responseType: 'blob' }
        );
    }

    markDistSubmitted(nachaDistBatchId: number): Observable<any> {
        return this.http.put<any>(`${this.base}/dist-batches/${nachaDistBatchId}/submit`, {});
    }

    listDistBatches(): Observable<any[]> {
        return this.http.get<any[]>(`${this.base}/dist-batches`);
    }

    /**
     * Hard-deletes a distribution batch + unlinks its statements so they can
     * be re-selected for a fresh batch. Refused server-side if the batch is
     * SUBMITTED. Returns { nachaDistBatchId, status, statementsUnlinked }.
     */
    deleteDistBatch(nachaDistBatchId: number): Observable<any> {
        return this.http.delete<any>(`${this.base}/dist-batches/${nachaDistBatchId}`);
    }
}
